/**
 * Escalation Settings — the tenant's own phrases for handing a WhatsApp conversation
 * to a team member, plus the master switch over automatic escalation.
 *
 * Most controls save on change (partial PATCH, mirroring AutomationSettings.tsx) rather
 * than collecting a dirty form behind a Save button — there's no multi-field validation
 * here that would need a single atomic submit, and immediate saves mean no
 * unsaved-changes trap. The server echoes back the normalised list (trimmed, deduped)
 * and that echo is what renders, so what's on screen is always exactly what the bot
 * will actually match.
 *
 * The one exception is the free-text handoff message: a click or a keystroke is its own
 * visible save signal for a switch/chip/radio/slider, but there's no equivalent gesture
 * for "done editing a paragraph" — so that field alone gets an explicit Save/Discard
 * pair instead of saving on blur.
 */
import { useRoutingSettings } from '@/hooks/useRoutingSettings';
import type {
  EscalationSettings as EscalationSettingsValue,
  RoutingPhraseTestResult,
} from '@/services/admin.api';
import userManagementService, { type Role } from '@/services/user-management.service';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';

interface PhraseListEditorProps {
  label: string;
  description: string;
  placeholder: string;
  phrases: string[];
  disabled: boolean;
  maxPhrases: number;
  maxLength: number;
  emptyHint: string;
  onChange: (next: string[]) => void;
}

/**
 * Chip list + add field. The validation here mirrors what the server enforces
 * (case-insensitive dedupe, length, count) purely so the user finds out as they type
 * instead of after a round trip — the server stays authoritative either way.
 */
const PhraseListEditor: React.FC<PhraseListEditorProps> = ({
  label,
  description,
  placeholder,
  phrases,
  disabled,
  maxPhrases,
  maxLength,
  emptyHint,
  onChange,
}) => {
  const [draft, setDraft] = React.useState('');
  const trimmed = draft.trim();

  const isDuplicate = phrases.some((p) => p.toLowerCase() === trimmed.toLowerCase());
  const isTooLong = trimmed.length > maxLength;
  const isFull = phrases.length >= maxPhrases;
  const canAdd = Boolean(trimmed) && !isDuplicate && !isTooLong && !isFull && !disabled;

  const helperText = isDuplicate
    ? 'You already added that phrase'
    : isTooLong
      ? `Keep phrases to ${maxLength} characters or fewer`
      : isFull
        ? `You've reached the limit of ${maxPhrases} phrases`
        : ' ';

  const commit = () => {
    if (!canAdd) return;
    onChange([...phrases, trimmed]);
    setDraft('');
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {description}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          fullWidth
          value={draft}
          placeholder={placeholder}
          disabled={disabled || isFull}
          error={isDuplicate || isTooLong}
          helperText={helperText}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
        />
        <Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={!canAdd}
            onClick={commit}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add
          </Button>
        </Box>
      </Stack>

      {phrases.length === 0 ? (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          {emptyHint}
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {phrases.map((phrase) => (
            <Chip
              key={phrase}
              label={phrase}
              size="small"
              disabled={disabled}
              onDelete={() => onChange(phrases.filter((p) => p !== phrase))}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

/**
 * Rule preview — type a message, see whether the bot would hand it over and why.
 * The industry-standard affordance (Zendesk/Chatwoot both ship one); without it the
 * only way to find out is to send a real WhatsApp and watch.
 *
 * Always evaluates against the SAVED config, server-side, using the same evaluator the
 * webhook runs — so it can never claim something the bot won't actually do.
 */
const PhraseTester: React.FC<{
  testing: boolean;
  onTest: (message: string) => Promise<RoutingPhraseTestResult | null>;
}> = ({ testing, onTest }) => {
  const [draft, setDraft] = React.useState('');
  const [result, setResult] = React.useState<RoutingPhraseTestResult | null>(null);

  const run = async () => {
    if (!draft.trim() || testing) return;
    setResult(await onTest(draft));
  };

  const describe = (r: RoutingPhraseTestResult): { severity: 'success' | 'info' | 'warning'; text: string } => {
    switch (r.reason) {
      case 'matched_tenant':
        return { severity: 'success', text: `Handed to a human — matched your phrase "${r.matchedPhrase}".` };
      case 'matched_builtin':
        return {
          severity: 'success',
          text: `Handed to a human — matched the built-in phrase "${r.matchedPhrase}".`,
        };
      case 'excluded':
        return {
          severity: 'warning',
          text: `Stays with the bot — your exception "${r.excludedBy}" applies, which overrides any phrase match.`,
        };
      case 'disabled':
        return { severity: 'warning', text: 'Stays with the bot — automatic handoff is switched off above.' };
      default:
        return { severity: 'info', text: 'Stays with the bot — nothing in this message matches.' };
    }
  };

  const verdict = result ? describe(result) : null;

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600}>
        Try a message
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Check what the bot would do with a message, using your saved settings. Nothing is
        sent and no chat is affected.
      </Typography>

      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          fullWidth
          value={draft}
          placeholder="e.g. hi, can I speak to manager please"
          disabled={testing}
          onChange={(e) => {
            setDraft(e.target.value);
            setResult(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void run();
            }
          }}
        />
        <Box>
          <Button
            variant="outlined"
            startIcon={testing ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            disabled={!draft.trim() || testing}
            onClick={() => void run()}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Test
          </Button>
        </Box>
      </Stack>

      {verdict && (
        <Alert severity={verdict.severity} sx={{ mt: 1.5 }}>
          {verdict.text}
        </Alert>
      )}
    </Box>
  );
};

const EscalationSettings: React.FC = () => {
  const {
    escalation,
    builtinExamples,
    defaults,
    limits,
    loading,
    saving,
    testing,
    error,
    success,
    save,
    testPhrase,
    dismissSuccess,
  } = useRoutingSettings();

  const patch = (next: Partial<EscalationSettingsValue>) => {
    void save(next);
  };

  // Local echo for the free-text/slider controls: unlike a switch or a chip list, these
  // shouldn't save on every keystroke or drag frame, so they hold a draft and commit on
  // blur / drag-end. Re-synced whenever the server's value changes under us.
  const [messageDraft, setMessageDraft] = React.useState(escalation.handoffMessage);
  const [thresholdDraft, setThresholdDraft] = React.useState(escalation.lowConfidenceThreshold);
  React.useEffect(() => setMessageDraft(escalation.handoffMessage), [escalation.handoffMessage]);
  React.useEffect(
    () => setThresholdDraft(escalation.lowConfidenceThreshold),
    [escalation.lowConfidenceThreshold]
  );
  const messageDirty = messageDraft.trim() !== escalation.handoffMessage.trim();
  const [aiFailureDraft, setAiFailureDraft] = React.useState(escalation.aiFailureMessage);
  React.useEffect(() => setAiFailureDraft(escalation.aiFailureMessage), [escalation.aiFailureMessage]);
  const aiFailureDirty = aiFailureDraft.trim() !== escalation.aiFailureMessage.trim();

  // The one genuinely dangerous combination: 'replace' removes the built-in phrases,
  // so with an empty list nothing automatic is left to catch a customer asking for help.
  const silentlyOff = escalation.mode === 'replace' && escalation.keywords.length === 0;

  // Fetched once, not re-derived from useRoutingSettings — this is the tenant's Role
  // roster (Settings → Roles), a different resource from anything the routing-settings
  // endpoint returns. Only needed to label/pick roles for the assignment-target control
  // below, so a fetch failure degrades to an empty picker rather than blocking the page.
  const [roles, setRoles] = React.useState<Role[]>([]);
  React.useEffect(() => {
    userManagementService
      .getRoles()
      .then((data) => setRoles(data || []))
      .catch(() => setRoles([]));
  }, []);
  const selectedRoles = roles.filter(
    (r) => escalation.assignmentTarget.kind === 'role' && escalation.assignmentTarget.roleIds.includes(r._id)
  );
  // Separate from escalation.assignmentTarget.kind itself: picking "A specific role"
  // must reveal the role picker immediately, before anything is saved (the server
  // rejects an empty roleIds, so there's nothing valid to save yet) — driving the
  // radio off the saved value alone would otherwise snap it straight back to
  // "Anyone" the instant it's clicked. Re-synced whenever the saved value changes,
  // same pattern as messageDraft/thresholdDraft above.
  const [targetKindDraft, setTargetKindDraft] = React.useState(escalation.assignmentTarget.kind);
  React.useEffect(
    () => setTargetKindDraft(escalation.assignmentTarget.kind),
    [escalation.assignmentTarget.kind]
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Human handoff
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          When a customer's WhatsApp message matches one of these phrases, the bot stops
          replying and the chat is handed to a team member — assigned automatically, or
          flagged as needing someone if nobody is available. Anyone on your team can
          still claim or assign a chat by hand from Chat Logs at any time.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
          These phrases are checked first, before any flow you build in Automation → Message
          Routing — a customer typing one of these always reaches a human, even mid-flow.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={dismissSuccess}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={escalation.enabled}
                    disabled={saving}
                    onChange={(e) => patch({ enabled: e.target.checked })}
                  />
                }
                label={
                  escalation.enabled
                    ? 'Hand chats to a human automatically'
                    : 'Automatic handoff is off'
                }
              />
              <Typography variant="body2" color="text.secondary">
                Turning this off stops both automatic triggers — the phrases below, and
                the bot handing over when it repeatedly can't answer confidently.
                Claiming and assigning chats by hand keeps working.
              </Typography>
            </Box>

            <Divider />

            {/* Everything below stays editable while handoff is off — a tenant who
                switches it off to pause still needs to fix or prepare their list, and
                greying it out would strand them. The note says what off actually means. */}
            <Box>
              {!escalation.enabled && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Automatic handoff is off, so none of the settings below are running right
                  now. You can still edit them — they take effect as soon as you switch it
                  back on.
                </Alert>
              )}
              <Stack spacing={3}>
                <PhraseListEditor
                  label="Your phrases"
                  description="Matched on whole words, ignoring capitalisation. A chat is handed over as soon as a message contains any one of them."
                  placeholder="e.g. speak to manager"
                  phrases={escalation.keywords}
                  disabled={saving}
                  maxPhrases={limits.maxKeywords}
                  maxLength={limits.maxKeywordLength}
                  emptyHint="No phrases of your own yet."
                  onChange={(keywords) => patch({ keywords })}
                />

                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Built-in phrases
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Common English ways of asking for a person, recognised out of the box.
                  </Typography>
                  <FormControl disabled={saving}>
                    <RadioGroup
                      value={escalation.mode}
                      onChange={(e) => patch({ mode: e.target.value as 'extend' | 'replace' })}
                    >
                      <FormControlLabel
                        value="extend"
                        control={<Radio size="small" />}
                        label="Use them alongside my phrases"
                      />
                      <FormControlLabel
                        value="replace"
                        control={<Radio size="small" />}
                        label="Ignore them — use only my phrases"
                      />
                    </RadioGroup>
                  </FormControl>

                  {escalation.mode === 'extend' && builtinExamples.length > 0 && (
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                      {builtinExamples.map((example) => (
                        <Chip
                          key={example}
                          label={example}
                          size="small"
                          variant="outlined"
                          sx={{ fontStyle: 'italic' }}
                        />
                      ))}
                      <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                        and similar wordings
                      </Typography>
                    </Stack>
                  )}

                  {silentlyOff && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Nothing will hand a chat over automatically right now — the built-in
                      phrases are off and you haven't added any of your own. Add a phrase,
                      or switch back to using the built-in ones.
                    </Alert>
                  )}
                </Box>

                <Divider />

                <PhraseListEditor
                  label="Never hand over when the message says"
                  description="Checked first — a message containing any of these stays with the bot even if it also matches a phrase above. Useful when a phrase catches something it shouldn't, like praise mentioning your customer service team."
                  placeholder="e.g. customer service team was great"
                  phrases={escalation.exclusions}
                  disabled={saving}
                  maxPhrases={limits.maxKeywords}
                  maxLength={limits.maxKeywordLength}
                  emptyHint="No exceptions set."
                  onChange={(exclusions) => patch({ exclusions })}
                />

                <Divider />

                <PhraseTester testing={testing} onTest={testPhrase} />

                <Divider />

                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    What the customer is told
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Sent once, automatically, the moment a chat is handed over. Leave blank
                    to use the default.
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={messageDraft}
                    placeholder={defaults.handoffMessage}
                    disabled={saving}
                    inputProps={{ maxLength: limits.maxHandoffMessageLength }}
                    helperText={
                      messageDraft.trim()
                        ? `${messageDraft.length}/${limits.maxHandoffMessageLength}`
                        : 'Using the default shown above.'
                    }
                    onChange={(e) => setMessageDraft(e.target.value)}
                  />
                  {/* Explicit action, not blur-to-save: unlike the switch/chips/radio/slider
                      above (where the click IS the save, visibly), typing a paragraph and
                      clicking elsewhere has no established "this saved" signal — a prior
                      version relied on blur alone and a saved edit was indistinguishable
                      from one that silently didn't take. */}
                  {messageDirty && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
                        onClick={() => patch({ handoffMessage: messageDraft })}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        disabled={saving}
                        onClick={() => setMessageDraft(escalation.handoffMessage)}
                      >
                        Discard
                      </Button>
                    </Stack>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    If the AI can&apos;t reply
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Sent to the customer when the AI service fails or times out and no reply
                    could be generated. Leave blank to use the default.
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={aiFailureDraft}
                    placeholder={defaults.aiFailureMessage}
                    disabled={saving}
                    inputProps={{ maxLength: limits.maxAiFailureMessageLength }}
                    helperText={
                      aiFailureDraft.trim()
                        ? `${aiFailureDraft.length}/${limits.maxAiFailureMessageLength}`
                        : 'Using the default shown above.'
                    }
                    onChange={(e) => setAiFailureDraft(e.target.value)}
                  />
                  {aiFailureDirty && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
                        onClick={() => patch({ aiFailureMessage: aiFailureDraft })}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        disabled={saving}
                        onClick={() => setAiFailureDraft(escalation.aiFailureMessage)}
                      >
                        Discard
                      </Button>
                    </Stack>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Hand over when the bot keeps struggling
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Separately from the phrases above, a chat is handed over when the bot
                    answers with weak confidence twice in a row. Raise this to hand over
                    sooner, lower it to hand over less often. Set it to 0 to switch this
                    trigger off and rely on phrases alone.
                  </Typography>
                  <Box sx={{ px: 1, maxWidth: 420 }}>
                    <Slider
                      value={thresholdDraft}
                      min={0}
                      max={100}
                      step={5}
                      marks={[
                        { value: 0, label: 'Off' },
                        { value: defaults.lowConfidenceThreshold, label: 'Default' },
                        { value: 100, label: 'Always' },
                      ]}
                      valueLabelDisplay="auto"
                      disabled={saving}
                      onChange={(_, value) => setThresholdDraft(value as number)}
                      // Commits on drag-end, not on every frame of the drag.
                      onChangeCommitted={(_, value) => {
                        if ((value as number) !== escalation.lowConfidenceThreshold) {
                          patch({ lowConfidenceThreshold: value as number });
                        }
                      }}
                    />
                  </Box>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Who receives an automatic handoff
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Applies to a customer asking for a human, the bot struggling above, and
                    an AI agent choosing to hand off. A "Send to a Team Member" automation
                    step has its own separate "Assign to" choice and doesn't use this setting.
                  </Typography>
                  <RadioGroup
                    value={targetKindDraft}
                    onChange={(_, value) => {
                      if (value === 'anyone') {
                        setTargetKindDraft('anyone');
                        patch({ assignmentTarget: { kind: 'anyone' } });
                      } else {
                        // Reveal the picker only — nothing valid to save until a role is
                        // actually chosen below (an empty roleIds is rejected server-side).
                        setTargetKindDraft('role');
                      }
                    }}
                  >
                    <FormControlLabel value="anyone" control={<Radio size="small" />} label="Anyone on your team" />
                    <FormControlLabel
                      value="role"
                      control={<Radio size="small" />}
                      label="A specific role"
                      disabled={saving}
                    />
                  </RadioGroup>
                  {targetKindDraft === 'role' && (
                    <Box sx={{ pl: 4, mt: 0.5, maxWidth: 420 }}>
                      <Autocomplete
                        multiple
                        size="small"
                        options={roles}
                        value={selectedRoles}
                        disabled={saving}
                        isOptionEqualToValue={(a, b) => a._id === b._id}
                        getOptionLabel={(r) => r.name}
                        onChange={(_, value) => {
                          if (!value.length) {
                            // Cleared back to nothing picked — that can't be saved as
                            // 'role' (empty roleIds), so fall back to 'anyone' rather
                            // than leaving an invisible, unsaved half-state.
                            setTargetKindDraft('anyone');
                            patch({ assignmentTarget: { kind: 'anyone' } });
                            return;
                          }
                          patch({ assignmentTarget: { kind: 'role', roleIds: value.map((r) => r._id) } });
                        }}
                        renderTags={(value, getTagProps) =>
                          value.map((r, index) => (
                            <Chip {...getTagProps({ index })} key={r._id} size="small" label={r.name} />
                          ))
                        }
                        renderInput={(params) =>
                          selectedRoles.length ? (
                            <TextField {...(params as any)} />
                          ) : (
                            <TextField {...(params as any)} placeholder="Choose one or more roles" />
                          )
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        If nobody in these roles is available, the chat goes to the account
                        owner instead, and you'll get an alert.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Stack>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default EscalationSettings;
